import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { useTheme } from "@material-ui/core/styles";
import { toast } from "react-toastify";
import { API } from "../services/api-services";
import { decrypt } from "../utils/other";
import { getUser } from "../utils/auth";
import { reducer_insertData } from "../Redux/Actions/studentInfo";
import {
  SANDBOX_FLAG,
  SIDEBAR_EVENT,
  isPaidCached,
} from "./usePaidAccessGuard";
import styles from "../AppRouter.module.css";

/**
 * Encapsulates all state, derived values, and side-effects for the ProtectedRoute component.
 * @returns {{ state: object, derived: object, handlers: object, productTour: object }}
 */
function useProtectedRoute() {
  const theme = useTheme();
  const location = useLocation();
  const user = getUser();
  const dispatch = useDispatch();

  const [showProfiles, setShowProfiles] = useState(false);
  const [profileData, setProfileData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errFetchUsers, setErrFetchUsers] = useState(false);

  const [tourIndex, setTourIndex] = useState(0);
  const [skip, setSkip] = useState(false);
  const [onBoardingComplete, setOnBoardingComplete] = useState(false);
  const productTourStatus = localStorage.getItem("productTourStatus");

  // Whether the student is paid. Determined by usePaidAccessGuard (cookie-cached
  // /student/is-paid) and announced via SIDEBAR_EVENT. Read from the user-bound
  // cookie so a cached paid status renders the sidebar on first paint with no flicker.
  const [paid, setPaid] = useState(() => isPaidCached());

  useEffect(() => {
    const sync = () => setPaid(isPaidCached());
    window.addEventListener(SIDEBAR_EVENT, sync);
    return () => window.removeEventListener(SIDEBAR_EVENT, sync);
  }, []);

  const mobileDevice = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    if (localStorage.getItem("user")) {
      let local = decrypt(localStorage.getItem("user"));
      const obj = {
        studentName: local?.studentName,
        profileImage: local?.profilePicUrl,
        initial: local?.studentName?.charAt(0),
        source: local?.source,
      };
      dispatch(dispatch(reducer_insertData(obj)));
    }
  }, [dispatch]);

  useEffect(() => {
    if (JSON.parse(localStorage.getItem("profileSwitched")) == true) {
      localStorage.setItem("profileSwitched", false);
      if (!window.ReactNativeWebView) {
        toast.success("Profile is switched successfully", {
          position: toast.POSITION.TOP_CENTER,
        });
      }
    }
  }, []);

  useEffect(() => {
    if (!user?.hasOwnProperty("parentId") && !window.ReactNativeWebView) {
      setLoading(true);
      API.get("/fetchProfiles")
        .then((res) => {
          setProfileData(res.data.profiles);
          setLoading(false);
        })
        .catch((e) => {
          setErrFetchUsers(true);
          setLoading(false);
        });
    }
  }, [user?.studentEmail]);

  useEffect(() => {
    if (location.pathname === "/book-a-demo") {
      window.document.body.style.backgroundColor = "#fff !important";
    }
  }, [location.pathname]);

  const pathname = location.pathname;
  const pathLower = pathname.toLowerCase();

  const isBuyCreditsPage = pathLower.includes("buy-credits");
  const isViewSchedulePage = pathLower.includes("viewschedule");
  const showSwitchProfile = profileData?.length > 1;

  // When the user arrived via the Sandbox → SP auth bridge (cross-domain redirect
  // from sandbox.codeyoung.com), we hide all nav chrome for the entire tab session.
  // sessionStorage is tab-scoped so it never affects independent SP sessions.
  // Exception: once such a user has class credits they are a real student, so we
  // drop the immersive treatment and show the sidebar like any other student.
  const fromSandbox = sessionStorage.getItem(SANDBOX_FLAG) === "1";
  const sandboxImmersive = fromSandbox && !paid;
  const isDashboardPage = pathLower.startsWith("/dashboard");

  useEffect(() => {
    if (sandboxImmersive) {
      document.body.classList.add("cy-sandbox-view");
      return () => document.body.classList.remove("cy-sandbox-view");
    }
  }, [sandboxImmersive]);

  const showSidebar =
    !sandboxImmersive &&
    !pathname.includes("chatwithus") &&
    !pathname.includes("class-summary") &&
    !pathname.includes("raise-request") &&
    !pathname.includes("addRescheduleClass") &&
    !window.ReactNativeWebView;

  const showDesktopHeader =
    !sandboxImmersive &&
    !mobileDevice &&
    !pathname.includes("community") &&
    !pathname.includes("class-summary") &&
    !pathname.includes("resources") &&
    !pathLower.includes("viewschedule") &&
    !pathname.includes("addRescheduleClass") &&
    !pathLower.includes("dashboard") &&
    !pathname.startsWith("/sb") &&
    !pathLower.includes("referrals/redeem-rewards") &&
    !isBuyCreditsPage &&
    !window.ReactNativeWebView;

  const layoutClass = mobileDevice
    ? styles.layoutRootMobile
    : styles.layoutRoot;

  const contentClass = sandboxImmersive
    ? styles.contentAreaCentered
    : !mobileDevice && !window.ReactNativeWebView && isViewSchedulePage
      ? styles.contentAreaFullAutoHeight
      : !mobileDevice &&
          !window.ReactNativeWebView &&
          !pathname.includes("addRescheduleClass") &&
          !isBuyCreditsPage
        ? isDashboardPage
          ? styles.contentAreaDashboard
          : styles.contentArea
        : styles.contentAreaFull;

  const buyCreditsClass = isBuyCreditsPage ? styles.buyCreditsPage : "";

  const backgroundForPath = ![
    "community",
    "book-a-demo",
    "addRescheduleClass",
  ].includes(pathname.replace("/", ""))
    ? "#F7FBFB"
    : "#FFF";

  return {
    state: { showProfiles, profileData, errFetchUsers, loading },
    derived: {
      mobileDevice,
      isBuyCreditsPage,
      showSwitchProfile,
      showSidebar,
      showDesktopHeader,
      layoutClass,
      contentClass,
      buyCreditsClass,
      backgroundForPath,
    },
    handlers: { setShowProfiles, setLoading },
    productTour: { tourIndex, skip, productTourStatus, onBoardingComplete },
  };
}

export default useProtectedRoute;
